import Box from '~/common/components/Box';
import Flex from '~/common/components/Flex';
import ListItem from '~/common/components/Lists/ListItem';
import OrderedList from '~/common/components/Lists/OrderedList';
import Text from '~/common/components/Texts/Text';
import { formatCurrency } from '~/common/util/format';
import { FormTypes, FormulaType } from '~/pages/IncomeCalculator/enums';
import type { CalculationResult } from '~/pages/IncomeCalculator/types';
import { pixelsToRem } from '~/theme/typography';

import BreakdownContainer from '../BreakdownContainer';
import lang from '../language.json';
import BreakdownColumnBox from '../styles/BreakdownColumnBox';
import BreakdownValue from '../styles/BreakdownValue';
import BreakdownYearContainer from '../styles/BreakdownYearContainer';
import SingleLineValue from '../styles/SingleLineValue';
import Continuance from './Continuance';

const inputForm = FormTypes.Form1040;

const Hr = (props) => (
  <Box {...props} role="separator" borderBottom="1px solid black" h={1} />
);

function mapFormulaForSingleYear(formulaId: number) {
  switch (formulaId) {
    case FormulaType.IRADistributionsForm1040OneYear:
    case FormulaType.IRADistributionsForm1040TwoYear:
      return [lang.FORMULAS.IRA_1040_CONV, '.25'];
    case FormulaType.IRADistributionsForm1040OneYearFHA:
    case FormulaType.IRADistributionsForm1040TwoYearFHA:
      return [lang.FORMULAS.IRA_1040_FHA, '.15'];
    default:
      return ['', ''];
  }
}

type IRA1040BreakdownProps = Readonly<{
  selectedResult: CalculationResult;
  hideAccordion?: boolean;
  onNewWindow?: (...args: any[]) => any;
}>;

type OneYearBreakdownProps = Readonly<{
  year: number;
}>;

export default function IRA1040Breakdown({
  selectedResult,
  hideAccordion = false,
  onNewWindow,
}: IRA1040BreakdownProps) {
  const fiscalYears = selectedResult.getFiscalYears().sort((a, b) => b - a);
  const formula = selectedResult.formulas[0];
  const [equation, grossUp] = mapFormulaForSingleYear(formula.formulaId);
  const annuals: Record<number, number> = {};

  function OneYearBreakdown({ year }: OneYearBreakdownProps) {
    const distributions = selectedResult.getInput(
      inputForm,
      year,
      'iraDistributions',
    )?.fieldValue;
    const taxable = selectedResult.getInput(
      inputForm,
      year,
      'taxableAmount',
    )?.fieldValue;
    const subtotal =
      Number.parseFloat(distributions) - Number.parseFloat(taxable);
    const grossedUp = subtotal * Number.parseFloat(grossUp);
    annuals[year] = Number.parseFloat(distributions) + grossedUp;

    return (
      <BreakdownYearContainer title={year} flexGrow={1} pb={4}>
        <SingleLineValue title={lang.EQUATION} value={equation} />
        <BreakdownColumnBox title={lang.CALCULATIONS}>
          <OrderedList fontSize={pixelsToRem(14)}>
            <ListItem>
              {formatCurrency(distributions)} - {formatCurrency(taxable)} ={' '}
              {formatCurrency(subtotal)} (Net non-taxable income)
            </ListItem>
            <ListItem>
              {formatCurrency(subtotal)} * {grossUp} ={' '}
              {formatCurrency(grossedUp)} (Grossed up amount)
            </ListItem>
            <ListItem>
              {formatCurrency(distributions)} + {formatCurrency(grossedUp)} ={' '}
              {formatCurrency(annuals[year])} (Annual amount)
            </ListItem>
          </OrderedList>
        </BreakdownColumnBox>
        <Hr />
        {formatCurrency(annuals[year])}
      </BreakdownYearContainer>
    );
  }

  function MonthlyIncomeBreakdown() {
    const total = Object.values(annuals).reduce((acc, value) => acc + value, 0);
    const years = Object.keys(annuals).length;

    return (
      <Box mb={4}>
        <Text fontWeight="bold" fontSize={pixelsToRem(14)} mb={2}>
          {lang.MONTHLY_CALC}
        </Text>
        {years > 1 && (
          <Box sx={{ display: 'inline-block' }}>
            {Object.values(annuals).map((annual, i) => (
              <BreakdownValue
                value={annual}
                isChild={i > 0}
                isCurrency
                pr={4}
                // eslint-disable-next-line @eslint-react/no-array-index-key
                key={i}
              />
            ))}
            <Hr my={1} />
          </Box>
        )}
        <Text fontSize="small">
          {formatCurrency(total)} / {years * 12} (months)
        </Text>
      </Box>
    );
  }

  return (
    <BreakdownContainer
      header={lang.HEADERS[selectedResult.calculatorTypeId]}
      hideAccordion={hideAccordion}
      onNewWindow={onNewWindow}
    >
      <Flex justifyContent="space-between" gridGap={4} mb={4}>
        {fiscalYears.map((fy) => (
          <OneYearBreakdown year={fy} key={fy} />
        ))}
      </Flex>
      <MonthlyIncomeBreakdown />
      <SingleLineValue
        title={lang.MONTHLY_INCOME}
        value={selectedResult.calculationAmount}
        isCurrency
      />
      <Continuance selectedResult={selectedResult} />
    </BreakdownContainer>
  );
}
